import { useMutation, useQuery } from "react-query";
import axiosInstance from '../api/_http';
import { ApiResponse } from "../libs/apiResponse";

// Define the types for notification
interface Notification {
  id?: number;
  title: string;
  description: string;
  createdAt?: string;
  typeId: number;
  sendBulkEmails?: boolean;
}

interface NotificationType {
  id: number;
  name: string;
}

// Fetch function for React Query
const fetchNotificationTypesData = async (): Promise<ApiResponse<NotificationType[]>> => {
  const { data } = await axiosInstance.get('/api/2.0/news-and-updates/types');
  return data;
};

const fetchNewsUpdatesByTypeId = async (typeID: number): Promise<ApiResponse<Notification[]>> => {
  const { data } = await axiosInstance.get(`/api/2.0/news-and-updates?type_id=${typeID}`);
  return data;
};

const fetchUnreadNotifications = async (userID: number): Promise<ApiResponse<Notification[]>> => {
  const { data } = await axiosInstance.get(`/api/2.0/news-and-updates/unread?user_id=${userID}`);
  return data;
};

// Custom hook to manage fetching notification types and mutating notifications
export const useNotificationManager = (isLoggedIn: boolean) => {
  // Fetch notification types
  const { data: notificationTypes, isLoading, isError } = useQuery<ApiResponse<NotificationType[]>, Error>(
    'notification-types',
    fetchNotificationTypesData,
    {
      enabled: isLoggedIn, // Only run the query if the user is logged in
    }
  );

  // Fetch unread notifications
  const useFetchUnreadNotifications = (userID: number) => {
    return useQuery<ApiResponse<Notification[]>, Error>(
      ['unread-notifications', userID],
      () => fetchUnreadNotifications(userID),
      {
        enabled: isLoggedIn && !!userID, // Ensure the query is disabled if not logged in or userID is not provided
      }
    );
  };

  // Fetch notification types (refactored for conditional fetching)
  const useFetchNotificationTypes = () => {
    return useQuery<ApiResponse<NotificationType[]>, Error>(
      'notification-types',
      fetchNotificationTypesData,
      {
        enabled: isLoggedIn, // Conditional fetching based on login state
      }
    );
  };

  // Fetch news updates by typeId
  const useFetchNewsUpdates = (typeId: number) => {
    return useQuery<ApiResponse<Notification[]>, Error>(
      ['news-updates', typeId],
      () => fetchNewsUpdatesByTypeId(typeId),
      {
        enabled: isLoggedIn, // Conditional fetching based on login state and typeId
      }
    );
  };

  // Mutation to create a new notification
  const { mutate: createNotification, isLoading: isCreating } = useMutation(
    async (newNotification: Notification) => {
      const response = await axiosInstance.post('/api/admin/2.0/news-and-updates', {
        title: newNotification.title,
        description: newNotification.description,
        type_id: newNotification.typeId,
        send_bulk_emails: newNotification.sendBulkEmails,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        // Handle success
      },
      onError: (error) => {
        // Handle error
      },
    }
  );

  // Mutation to update last seen notification time
  const { mutate: updateLastSeen, isLoading: isUpdatingLastSeen, isError: isUpdatingLastSeenError } = useMutation(
    async (userId: number) => {
      const response = await axiosInstance.post('/api/news-and-updates/unread', { user_id: userId });
      return response.data;
    },
    {
      onSuccess: () => {
        // Handle success
      },
      onError: (error) => {
        // Handle error
      },
    }
  );

  return {
    notificationTypes: notificationTypes?.data,
    isLoading,
    isError,
    createNotification,
    isCreating,
    useFetchNewsUpdates,
    useFetchNotificationTypes,
    useFetchUnreadNotifications,
    updateLastSeen,
  };
};
